import { CollectionConfig } from "@rebasepro/types";
import companiesCollection from "./companies";

/**
 * Invitations to join a company account.
 *
 * The only way a second person gets into a company. `company_members` still
 * admits an INSERT only while a company has no members (`claim_unclaimed_company`),
 * and nothing about invitations changed that: an invitation is accepted by the
 * `company-invitation` function on the owner path, after it has matched the
 * token, checked that the account's address is the invited one and that the
 * address is verified. No rule here or there lets a caller write a membership.
 *
 * Every write to this table is a function's — `company-team` creates, resends
 * and withdraws; `company-invitation` accepts — which is why the only non-admin
 * rule is a read. A member sees their own company's invitations so the
 * "Equipo" list can show who is pending and let them withdraw one.
 *
 * The link in the email carries 32 random bytes. Only their SHA-256 is stored
 * (`token_hash`), and that column is kept off the API as well, so reading this
 * table — which every member of the company can — never yields a working link.
 *
 * Table, CHECKs, the one-open-invitation-per-address index and RLS in
 * drizzle/0070.
 */
const companyInvitationsCollection: CollectionConfig = {
    name: "Invitaciones a empresas",
    singularName: "Invitación a empresa",
    slug: "company-invitations",
    table: "company_invitations",
    // Own this table's RLS outright, as company_leads does. The injected
    // defaults admit `auth.uid() IS NULL`, and a table holding the way into a
    // company should not regain a policy nobody wrote on the next boot. The two
    // functions write on the owner connection, which the policies do not bind.
    disableDefaultPolicies: true,
    securityRules: [
        {
            // Your own company's invitations. The company_members subquery runs
            // under the caller's RLS and names the caller's uid, so only a real
            // membership satisfies it — `member_read_own_lead`'s idiom.
            name: "member_read_own_company_invitations",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = company_invitations.company_id)"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        company_id: {
            name: "Empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            validation: { required: true },
            admin: { readOnly: true }
        },
        email: {
            name: "Correo invitado",
            type: "string",
            validation: { required: true },
            description: "La dirección a la que se envió. Solo una cuenta verificada con exactamente esta dirección puede aceptarla.",
            admin: { readOnly: true }
        },
        invited_by: {
            name: "Invitada por",
            type: "string",
            userSelect: true,
            validation: { required: true },
            admin: { readOnly: true }
        },
        token_hash: {
            name: "Huella del enlace",
            type: "string",
            // The SHA-256 of the emailed token. Not a working link — but there
            // is no screen that needs it, so it does not leave the server.
            excludeFromApi: true,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        created_at: {
            name: "Creada",
            type: "date",
            autoValue: "on_create"
        },
        sent_at: {
            name: "Correo enviado",
            type: "date",
            description: "Cuándo aceptó el correo el servidor por última vez. Vacío: la invitación existe y a esa persona no le ha llegado nada.",
            admin: { readOnly: true }
        },
        // drizzle/0070's column, which this config never declared: a database
        // built from it lacked the column, and 0070's own statements that read
        // it failed there. Written only by the invitation function.
        send_count: {
            name: "Envíos",
            type: "number",
            description: "Cuántas veces ha aceptado el servidor el correo de esta invitación.",
            defaultValue: 0,
            validation: { required: true },
            admin: { readOnly: true }
        },
        expires_at: {
            name: "Caduca",
            type: "date",
            validation: { required: true },
            admin: { readOnly: true }
        },
        accepted_at: {
            name: "Aceptada",
            type: "date",
            admin: { readOnly: true }
        },
        accepted_by: {
            name: "Aceptada por",
            type: "string",
            userSelect: true,
            admin: { readOnly: true }
        },
        revoked_at: {
            name: "Retirada",
            type: "date",
            admin: { readOnly: true }
        },
        revoked_by: {
            name: "Retirada por",
            type: "string",
            userSelect: true,
            admin: { readOnly: true }
        }
    },
    admin: {
        // Read on a company's cover (`views/CompanyOverview.tsx`), next to the
        // people who already have access — the only context in which an
        // invitation means anything. Out of the drawer for company_members'
        // reason.
        hideFromNavigation: true,
        group: "Empresas",
        icon: "MailPlus",
        sort: ["created_at", "desc"],
        listProperties: ["email", "company_id", "sent_at", "expires_at", "accepted_at", "revoked_at"],
        previewProperties: ["email", "company_id"]
    }
};

export default companyInvitationsCollection;
